import React, { useEffect, useState } from "react";
import { companyLogoUrl } from "../../../../apis/company-logo-apis/companyLogoApis";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  Link,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import {
  CARD_HEADER_SX,
  outlinedButtonSx,
  primaryButtonSx,
} from "../../../Organization/CodeSecurity/codeSecurityShared";
import { styles } from "../../styles/TrustCenter.styles";
import { OverviewStats } from "../../components/OverviewSection";
import { visibleControls } from "../../data/controls";
import ConfirmDialog from "../../../../components/ConfirmDialog/ConfirmDialog";
import BrandPreview from "./BrandPreview";
import { brandOf, heroTitleOf } from "../../data/brand";
import { Link as RouterLink } from "react-router-dom";
import {
  DialogHeader,
  dialogActionsSx,
  dialogPaperProps,
} from "../../components/DialogParts";
import OverviewForm, {
  tabWithError,
  overviewChanges,
  toOverviewForm,
  validateOverview,
} from "./OverviewForm";

const displayUrl = (url) => String(url || "").replace(/^https?:\/\//, "");

const Detail = ({ label, children }) => {
  const theme = useTheme();
  return (
    <Box sx={styles.detailTile}>
      <Typography
        variant="body2"
        sx={{ color: theme.customColors.text.secondary, mb: 0.5 }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
};

const ValueLink = ({ href, children }) => (
  <Link href={href} target="_blank" rel="noopener" variant="body2">
    {children}
  </Link>
);

const ValueText = ({ children }) => (
  <Typography variant="body2" sx={{ fontWeight: 500 }}>
    {children}
  </Typography>
);

// The Overview editor: a dialog in the middle of the screen, opened on one
// section by its card (Edit → Details, Edit branding → Branding; no switch
// between them). Details is one column of fields; Branding is wide, with a
// live preview of the public page beside its settings (stacked on small
// screens). Cancel and Save at the bottom. Closing with unsaved changes
// asks first.
const OverviewDialog = ({
  open,
  data,
  onSave,
  onClose,
  initialTab = "details",
}) => {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down("sm"));
  const [form, setForm] = useState(() => toOverviewForm(data));
  const [errors, setErrors] = useState({});
  const [tab, setTab] = useState("details");
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(toOverviewForm(data));
      setErrors({});
      setTab(initialTab);
    }
    // Reset only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isDirty = JSON.stringify(form) !== JSON.stringify(toOverviewForm(data));

  // Only this dialog's own fields are checked (Details or Branding; each
  // card opens its own).
  const handleSave = () => {
    const nextErrors = Object.fromEntries(
      Object.entries(validateOverview(form)).filter(
        ([key, value]) => tabWithError({ [key]: value }) === tab
      )
    );
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    onSave(overviewChanges(form, data));
    onClose();
  };
  const handleClose = () => (isDirty ? setConfirmDiscard(true) : onClose());
  const branding = tab === "branding";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={false}
      fullScreen={phone}
      PaperProps={{
        ...dialogPaperProps,
        "aria-label": branding ? "Edit branding" : "Edit overview",
        sx: {
          ...dialogPaperProps.sx,
          // Narrow for Details, wide for Branding (room for the preview).
          maxWidth: phone ? "100%" : branding ? 1200 : 560,
          transition: "max-width 220ms ease",
          // Branding needs a tall pane for the preview. Details fits its fields.
          height: branding ? { sm: "min(860px, 90vh)" } : "auto",
          maxHeight: phone ? "100%" : "90vh",
        },
      }}
    >
      {/* Same chrome as the app's dialogs (Risk's Add Risk): bold title,
          the explanation in an info box. */}
      <DialogHeader
        title={branding ? "Edit branding" : "Edit overview"}
        onClose={handleClose}
      />
      <DialogContent
        dividers
        sx={{ p: 0, display: "flex", flexDirection: "column" }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: branding
                ? "minmax(0, 440px) minmax(0, 1fr)"
                : "minmax(0, 1fr)",
            },
            gridTemplateRows: { xs: "auto auto", md: "minmax(0, 1fr)" },
          }}
        >
          {/* Settings: scroll on their own. */}
          <Box
            sx={{
              order: { xs: 2, md: 1 },
              p: 3,
              overflowY: { md: "auto" },
              borderRight: {
                md: branding
                  ? `1px solid ${theme.customColors.border.main}`
                  : "none",
              },
              minWidth: 0,
            }}
          >
            {/* An explanation, not an alert: no "alert" role. */}
            <Alert
              severity="info"
              role="note"
              sx={{
                mb: 3,
                backgroundColor: theme.customColors.background.info,
                border: `1px solid ${theme.customColors.border.accent}`,
              }}
            >
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ fontSize: "14px", color: theme.customColors.text.strong }}
              >
                {branding
                  ? "How your public page looks. The preview shows your changes as you make them; buyers see them after you save and publish."
                  : "What buyers read on your page. They see your changes after you save and publish."}
              </Typography>
            </Alert>
            <OverviewForm
              key={tab}
              form={form}
              set={(changes) => setForm((prev) => ({ ...prev, ...changes }))}
              errors={errors}
              tab={tab}
            />
          </Box>
          {/* Live preview, on Branding only: stays put while the settings
              scroll. */}
          {branding && (
            <Box
              sx={{
                order: { xs: 1, md: 2 },
                p: 3,
                overflowY: { md: "auto" },
                minWidth: 0,
                backgroundColor: theme.customColors.background.infoBox,
              }}
            >
              <BrandPreview
                form={form}
                logoPath={data.company.logoPath}
                frameworks={(data.frameworks || []).filter((f) => f.isVisible)}
                large
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={dialogActionsSx(theme)}>
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={outlinedButtonSx(theme)}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!isDirty}
          onClick={handleSave}
          sx={primaryButtonSx(theme)}
        >
          Save changes
        </Button>
      </DialogActions>
      <ConfirmDialog
        open={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false);
          onClose();
        }}
        heading="Discard your changes?"
        content="You have changes that are not saved. They will be lost."
        buttonText="Discard"
      />
    </Dialog>
  );
};

const STYLE_LABELS = { gradient: "Gradient", image: "Image", minimal: "None" };
const TEXT_LABELS = { auto: "Automatic", light: "Light", dark: "Dark" };

// A colour value with its swatch.
const Swatch = ({ color, note }) => {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: "5px",
          flexShrink: 0,
          backgroundColor: color,
          border: `1px solid ${theme.customColors.border.main}`,
        }}
      />
      <ValueText>
        {color}
        {note && (
          <Box
            component="span"
            sx={{ color: theme.customColors.text.secondary }}
          >
            {" "}
            {note}
          </Box>
        )}
      </ValueText>
    </Stack>
  );
};

// How the public page looks: a small preview of its top and the settings
// behind it. Edit branding opens the dialog on Branding.
const BrandingCard = ({ data, brand, onEdit }) => {
  const theme = useTheme();
  const { company } = data;
  const banner = STYLE_LABELS[brand.bannerStyle];
  const favicon = companyLogoUrl(company.faviconPath);
  return (
    <Card
      sx={{
        borderRadius: "12px",
        border: `1px solid ${theme.customColors.border.main}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      }}
    >
      <CardHeader
        sx={CARD_HEADER_SX}
        title={
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
            Branding
          </Typography>
        }
        subheader={
          <Typography
            variant="body2"
            sx={{ color: theme.customColors.text.secondary, mt: 0.25 }}
          >
            How your public page looks: banner, colours, font and browser icon.
          </Typography>
        }
        action={
          <Button
            variant="outlined"
            startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={onEdit}
            sx={outlinedButtonSx(theme)}
          >
            Edit branding
          </Button>
        }
      />
      <CardContent
        sx={{
          p: 3,
          display: "grid",
          gap: 3,
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(0, 1fr) minmax(0, 1fr)",
          },
          alignItems: "start",
        }}
      >
        <BrandPreview form={toOverviewForm(data)} logoPath={company.logoPath} />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1.5,
          }}
        >
          <Detail label="Banner">
            <ValueText>
              {banner}
              {brand.bannerStyle !== "minimal" &&
                ` · ${TEXT_LABELS[String(company.bannerTextColor || "auto").toLowerCase()] || "Automatic"} text`}
            </ValueText>
          </Detail>
          <Detail label="Font">
            <ValueText>{brand.font.name}</ValueText>
          </Detail>
          <Detail label="Primary colour">
            <Swatch
              color={brand.accent}
              note={company.brandColor ? "" : "(default)"}
            />
          </Detail>
          <Detail label="Secondary colour">
            <Swatch
              color={brand.secondary}
              note={company.secondaryColor ? "" : "(same as primary)"}
            />
          </Detail>
          {favicon && (
            <Detail label="Favicon">
              <Box
                component="img"
                src={favicon}
                alt="Favicon"
                sx={{ width: 24, height: 24, objectFit: "contain" }}
              />
            </Detail>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const OverviewTab = ({ data, onSave }) => {
  const theme = useTheme();
  // The dialog's tab to open (Details or Branding), or null when closed.
  const [editing, setEditing] = useState(null);
  const { company } = data;
  const brand = brandOf(company);
  const customLinks = company.customLinks || [];
  // A long overview shows its first lines with "Show more".
  const [overviewOpen, setOverviewOpen] = useState(false);
  const overview = (company.overview || "").trim();
  const longOverview = overview.length > 260 || overview.split("\n").length > 3;
  const visitorView = {
    ...data,
    frameworks: (data.frameworks || []).filter((f) => f.isVisible),
    controls: visibleControls(
      data.controls || [],
      data.controlVisibility,
      data.frameworks
    ),
    // What visitors see: manual vendors and integrations switched on.
    subprocessors: [
      ...(data.subprocessors || []),
      ...Object.values(data.integrationSubprocessors || {}),
    ].filter((s) => s.isVisible),
  };

  return (
    <Stack spacing={2}>
      <OverviewStats data={visitorView} />

      <Card
        sx={{
          borderRadius: "12px",
          border: `1px solid ${theme.customColors.border.main}`,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <CardHeader
          sx={CARD_HEADER_SX}
          title={
            <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
              Company banner
            </Typography>
          }
          subheader={
            <Typography
              variant="body2"
              sx={{ color: theme.customColors.text.secondary, mt: 0.25 }}
            >
              The first thing buyers read: your title, overview and how to reach
              you.
            </Typography>
          }
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                onClick={() => setEditing("details")}
                sx={outlinedButtonSx(theme)}
              >
                Edit
              </Button>
            </Stack>
          }
        />
        <CardContent sx={{ p: 3 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "flex-start" }}
          >
            <Box sx={styles.logoFrame}>
              {company.logoPath ? (
                <Box
                  component="img"
                  src={companyLogoUrl(company.logoPath)}
                  alt={`${company.name} logo`}
                  sx={{
                    maxWidth: "88%",
                    maxHeight: "80%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <ImageOutlinedIcon
                  sx={{ color: theme.customColors.icons.secondary }}
                />
              )}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: theme.customColors.text.strong }}
              >
                {heroTitleOf(company)}
              </Typography>
              <Typography
                sx={{
                  mt: 1,
                  color: theme.customColors.text.secondary,
                  lineHeight: 1.7,
                  whiteSpace: "pre-line",
                  ...(longOverview && !overviewOpen
                    ? {
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }
                    : {}),
                }}
              >
                {overview}
              </Typography>
              {longOverview && (
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => setOverviewOpen((open) => !open)}
                  aria-expanded={overviewOpen}
                  sx={{ mt: 0.5, fontWeight: 600 }}
                >
                  {overviewOpen ? "Show less" : "Show more"}
                </Link>
              )}
              {/* Not in the Edit dialog: say where they are changed. */}
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 1,
                  color: theme.customColors.text.secondary,
                }}
              >
                Company name, overview and website are edited in{" "}
                <Link
                  component={RouterLink}
                  to="/company-settings"
                  target="_blank"
                  rel="noopener"
                >
                  Company Settings
                </Link>
                .
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1.5,
              mt: 3,
            }}
          >
            {/* Only the details that are filled in; empty ones are left out. */}
            {company.website && (
              <Detail label="Website">
                <ValueLink href={company.website}>
                  {displayUrl(company.website)}
                </ValueLink>
              </Detail>
            )}
            {company.securityEmail && (
              <Detail label="Contact email">
                <ValueLink href={`mailto:${company.securityEmail}`}>
                  {company.securityEmail}
                </ValueLink>
              </Detail>
            )}
            {company.privacyUrl && (
              <Detail label="Privacy policy">
                <ValueLink href={company.privacyUrl}>
                  {displayUrl(company.privacyUrl)}
                </ValueLink>
              </Detail>
            )}
            {company.termsUrl && (
              <Detail label="Terms of service">
                <ValueLink href={company.termsUrl}>
                  {displayUrl(company.termsUrl)}
                </ValueLink>
              </Detail>
            )}
            {company.dataResidency && (
              <Detail label="Where customer data is stored">
                <ValueText>{company.dataResidency}</ValueText>
              </Detail>
            )}
            {customLinks.length > 0 && (
              <Detail label="Custom links">
                <Stack spacing={0.5}>
                  {customLinks.map((link) => (
                    <ValueLink key={link.id} href={link.url}>
                      {link.label}
                    </ValueLink>
                  ))}
                </Stack>
              </Detail>
            )}
          </Box>
        </CardContent>
      </Card>
      <BrandingCard
        data={data}
        brand={brand}
        onEdit={() => setEditing("branding")}
      />
      <OverviewDialog
        open={!!editing}
        initialTab={editing || "details"}
        data={data}
        onSave={onSave}
        onClose={() => setEditing(null)}
      />
    </Stack>
  );
};

export default OverviewTab;
